import React from 'react';
import { Box, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';

const Container = styled(Box)(({ theme }) => ({
  padding: '60px 40px',
  maxWidth: '900px',
  margin: '0 auto',
  [theme.breakpoints.down('md')]: {
    padding: '40px 30px',
  },
  [theme.breakpoints.down('sm')]: {
    padding: '30px 20px',
  },
}));

const Title = styled(Typography)(({ theme }) => ({
  fontSize: '42px',
  fontWeight: 'bold',
  color: '#FFFFFF',
  fontFamily: '"Press Start 2P", cursive',
  marginBottom: '40px',
  textShadow: '0 0 20px rgba(0, 240, 255, 0.5)',
  [theme.breakpoints.down('lg')]: {
    fontSize: '32px',
  },
  [theme.breakpoints.down('md')]: {
    fontSize: '24px',
    marginBottom: '30px',
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '18px',
    marginBottom: '20px',
  },
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontSize: '18px',
  fontWeight: 'bold',
  color: '#00F0FF',
  fontFamily: '"Press Start 2P", cursive',
  marginBottom: '15px',
  marginTop: '30px',
  [theme.breakpoints.down('md')]: {
    fontSize: '14px',
    marginTop: '20px',
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '12px',
    marginTop: '15px',
  },
}));

const Content = styled(Typography)(({ theme }) => ({
  fontSize: '14px',
  color: '#6B6A78',
  fontFamily: '"JetBrains Mono", monospace',
  lineHeight: 1.8,
  marginBottom: '15px',
  [theme.breakpoints.down('md')]: {
    fontSize: '12px',
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '11px',
  },
}));

const PrivacyPolicy: React.FC = () => {
  return (
    <Container>
      <Title>PRIVACY POLICY</Title>
      
      <Content>Last updated: June 18, 2024</Content>

      <SectionTitle>1. INTRODUCTION</SectionTitle>
      <Content>
        StegSafe is committed to protecting your privacy. This Privacy Policy explains how we handle 
        your data when you use our image steganography service.
      </Content>

      <SectionTitle>2. CLIENT-SIDE PROCESSING</SectionTitle>
      <Content>
        All image processing in StegSafe happens entirely on your device (client-side). Your images, 
        messages, and passwords are never uploaded to any server. We do not store, collect, or have 
        access to your data in any form.
      </Content>

      <SectionTitle>3. DATA COLLECTION</SectionTitle>
      <Content>
        StegSafe does not collect any personal data. We do not:
        <br />• Store your images or messages
        <br />• Track your usage patterns
        <br />• Collect IP addresses or device information
        <br />• Use cookies or tracking technologies
        <br />• Share data with third parties
      </Content>

      <SectionTitle>4. LOCAL STORAGE</SectionTitle>
      <Content>
        The application may use your browser's local storage to save preferences and settings. 
        This data remains on your device and is never transmitted to our servers.
      </Content>

      <SectionTitle>5. THIRD-PARTY SERVICES</SectionTitle>
      <Content>
        StegSafe does not use any third-party services that would have access to your data. 
        The application runs entirely in your browser without external dependencies.
      </Content>

      <SectionTitle>6. SECURITY</SectionTitle>
      <Content>
        Since all processing happens locally on your device, your data is as secure as your 
        device itself. We recommend:
        <br />• Keeping your device and browser updated
        <br />• Using strong, unique passwords
        <br />• Not sharing your stego images with unauthorized parties
      </Content>

      <SectionTitle>7. DATA RETENTION</SectionTitle>
      <Content>
        Since we do not collect or store your data, there is no data retention period. Your data 
        exists only on your device for as long as you choose to keep it.
      </Content>

      <SectionTitle>8. CHILDREN'S PRIVACY</SectionTitle>
      <Content>
        StegSafe is not directed to children under 13. Since we do not collect personal data, 
        this is not applicable to our service.
      </Content>

      <SectionTitle>9. CHANGES TO THIS POLICY</SectionTitle>
      <Content>
        We may update this Privacy Policy from time to time. Any changes will be posted on this 
        page with an updated revision date.
      </Content>

      <SectionTitle>10. CONTACT INFORMATION</SectionTitle>
      <Content>
        If you have questions about this Privacy Policy, please contact us through the appropriate 
        channels provided on the website.
      </Content>

      <Box sx={{ mt: 6, p: 3, border: '1px solid #39FF14', backgroundColor: 'rgba(57, 255, 20, 0.05)', borderRadius: '5px' }}>
        <Typography sx={{ color: '#39FF14', fontFamily: '"Press Start 2P", cursive', fontSize: '12px', mb: 2 }}>
          ZERO-KNOWLEDGE PRIVACY
        </Typography>
        <Typography sx={{ color: '#6B6A78', fontFamily: '"JetBrains Mono", monospace', fontSize: '14px' }}>
          StegSafe operates on a zero-knowledge principle. We have no way to access your images, 
          messages, or passwords because all processing happens locally in your browser. Your privacy 
          is guaranteed by design.
        </Typography>
      </Box>
    </Container>
  );
};

export default PrivacyPolicy;
